import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {HTMLElement | null} */
  accountPopover = null;

  /** @type {HTMLElement | null} */
  accountPopoverTrigger = null;

  /** @type {HTMLElement | null} */
  accountPopoverPanel = null;

  /** @type {HTMLButtonElement | null} */
  accountPopoverClose = null;

  /** @type {HTMLElement | null} */
  accountPortalLogin = null;

  #portalSession = 'unknown';
  #sessionRequest = null;
  #sessionAbort = null;
  #sessionEpoch = 0;
  #expiryTimer = null;
  #pollTimer = null;


  /** @type {HTMLElement | null} */
  accountPortalLogout = null;

  connectedCallback() {
    super.connectedCallback();
    this.accountPopover = this.querySelector('[data-account-popover]');
    this.accountPopoverTrigger = this.querySelector('[data-account-popover-trigger]');
    this.accountPopoverPanel = this.querySelector('[data-account-popover-panel]');
    this.accountPopoverClose = this.querySelector('[data-account-popover-close]');
    this.accountPortalLogin = this.querySelector('[data-account-portal-login]');
    this.accountPortalLogout = this.querySelector('[data-account-portal-logout]');

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('focus', this.#onSessionResume);
    window.addEventListener('pageshow', this.#onSessionResume);
    window.addEventListener('pagehide', this.#onSessionSuspend);
    document.addEventListener('visibilitychange', this.#onSessionVisibility);
    window.visualViewport?.addEventListener('resize', this.#onResize);
    window.visualViewport?.addEventListener('scroll', this.#onResize);
    this.accountPopoverTrigger?.addEventListener('click', this.#onAccountTriggerClick);
    this.accountPopoverClose?.addEventListener('click', this.#onAccountClose);

    this.#syncCartTriggerAriaExpanded();
    if (this.accountPopover) {
      this.#setPortalSession('unknown');
      void this.#refreshPortalSession();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('focus', this.#onSessionResume);
    window.removeEventListener('pageshow', this.#onSessionResume);
    window.removeEventListener('pagehide', this.#onSessionSuspend);
    document.removeEventListener('visibilitychange', this.#onSessionVisibility);
    window.visualViewport?.removeEventListener('resize', this.#onResize);
    window.visualViewport?.removeEventListener('scroll', this.#onResize);
    this.accountPopoverTrigger?.removeEventListener('click', this.#onAccountTriggerClick);
    this.accountPopoverClose?.removeEventListener('click', this.#onAccountClose);

    this.#onSessionSuspend();
  }

  #onSessionSuspend = () => {
    this.#sessionEpoch++;
    this.#sessionAbort?.abort();
    this.#sessionRequest = null;
    clearTimeout(this.#expiryTimer);
    clearTimeout(this.#pollTimer);
    this.#setAccountPopoverOpen(false);
    this.#setPortalSession('unknown');
  };

  #onSessionResume = () => {
    if (!this.accountPopover || document.visibilityState === 'hidden') return;
    if (this.#sessionRequest) {
      this.#sessionEpoch++;
      this.#sessionAbort?.abort();
      this.#sessionRequest = null;
    }
    return this.#refreshPortalSession();
  };

  #onSessionVisibility = () => {
    if (document.visibilityState !== 'hidden') void this.#onSessionResume();
  };

  #scheduleSessionPoll = () => {
    clearTimeout(this.#pollTimer);
    if (this.accountPopoverPanel?.hidden !== false) return;
    this.#pollTimer = setTimeout(async () => {
      if (document.visibilityState !== 'hidden') await this.#refreshPortalSession();
      this.#scheduleSessionPoll();
    }, 30000);
  };

  // Status only: no customer data, CSRF issuance or session-cookie rotation.
  #refreshPortalSession = () => {
    if (this.#sessionRequest) return this.#sessionRequest;
    const epoch = this.#sessionEpoch;
    const controller = new AbortController();
    this.#sessionAbort = controller;
    this.#sessionRequest = (async () => {
      let state = 'unknown';
      let expiresAt = 0;
      try {
        const response = await fetch('https://account-api.guedeslopes.pt/api/portal/session', {
          method: 'GET', credentials: 'include', cache: 'no-store', redirect: 'error',
          headers: { Accept: 'application/json' },
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(4000)]),
        });
        if (response.status !== 200 && response.status !== 401) throw new Error('Session unavailable');
        if (!response.headers.get('content-type')?.includes('application/json')) throw new Error('Invalid session response');
        const text = await response.text();
        if (text.length > 1024) throw new Error('Invalid session response');
        const value = JSON.parse(text);
        if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid session response');
        const keys = Object.keys(value).sort().join(',');
        if (response.status === 401 && keys === 'authenticated' && value.authenticated === false) state = 'anonymous';
        else if (response.status === 200 && keys === 'authenticated,expiresAt' && value.authenticated === true &&
          typeof value.expiresAt === 'string' && Date.parse(value.expiresAt) > Date.now()) {
          state = 'authenticated';
          expiresAt = Date.parse(value.expiresAt);
        }
      } catch {
        // An outage is unknown, not evidence that a customer must log in again.
      }
      if (epoch === this.#sessionEpoch && this.isConnected) {
        this.#setPortalSession(state);
        clearTimeout(this.#expiryTimer);
        if (state === 'authenticated') this.#expiryTimer = setTimeout(() => {
          this.#setPortalSession('anonymous');
          void this.#refreshPortalSession();
        }, Math.min(expiresAt - Date.now(), 2147483647));
      }
    })().finally(() => {
      if (epoch === this.#sessionEpoch) this.#sessionRequest = null;
    });
    return this.#sessionRequest;
  };

  #setPortalSession = (state) => {
    this.#portalSession = state;
    this.setAttribute('data-portal-session', state);
    if (this.accountPopover) this.accountPopover.hidden = false;
    const loginHadFocus = document.activeElement === this.accountPortalLogin;
    const logoutHadFocus = document.activeElement === this.accountPortalLogout;
    if (this.accountPortalLogin) this.accountPortalLogin.hidden = state !== 'anonymous';
    if (this.accountPortalLogout) this.accountPortalLogout.hidden = state !== 'authenticated';
    this.accountPopoverTrigger?.setAttribute('aria-busy', String(state === 'unknown'));
    if (state === 'unknown') {
      this.#setAccountPopoverOpen(false);
      if (loginHadFocus || logoutHadFocus) this.accountPopoverTrigger?.focus();
    } else if (state === 'authenticated' && loginHadFocus) {
      this.accountPortalLogout?.focus();
    } else if (state === 'anonymous' && logoutHadFocus) {
      this.accountPortalLogin?.focus();
    }
  };

  /**
   * Positions an account surface inside the viewport. The authenticated mobile
   * sheet is bottom anchored; desktop remains under the same trigger in all states.
   * @param {HTMLElement} trigger
   * @param {HTMLElement} surfaceHost
   * @param {number} preferredWidth
   */
  #positionAccountSurface = (trigger, surfaceHost, preferredWidth) => {
    const viewportGutter = 12;
    const surfaceGap = 6;
    const width = Math.min(preferredWidth, window.innerWidth - viewportGutter * 2);
    const triggerRect = trigger.getBoundingClientRect();
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - width / 2;
    const viewportLeft = Math.max(viewportGutter, Math.min(centeredLeft, window.innerWidth - width - viewportGutter));
    const isCustomPopover = surfaceHost.matches('[data-account-popover]');
    const containingBlock = isCustomPopover ? surfaceHost.closest('.header') : null;
    const containingBlockRect = containingBlock?.getBoundingClientRect();
    const coordinateLeft = containingBlockRect?.left ?? 0;
    const coordinateTop = containingBlockRect?.top ?? 0;
    const left = viewportLeft - coordinateLeft;
    const top = triggerRect.bottom + surfaceGap - coordinateTop;
    let resolvedTop = top;

    if (isCustomPopover && window.matchMedia('(max-width: 749px)').matches) {
      const panel = surfaceHost.querySelector('[data-account-popover-panel]');
      const panelRect = panel?.getBoundingClientRect();
      const viewportBottom =
        (window.visualViewport?.offsetTop ?? 0) + (window.visualViewport?.height ?? window.innerHeight);

      if (panelRect?.height) {
        resolvedTop = viewportBottom - panelRect.height - viewportGutter - coordinateTop;
      }
    }

    surfaceHost.style.setProperty('--agl-account-dialog-left', `${left}px`);
    surfaceHost.style.setProperty('--agl-account-dialog-width', `${width}px`);
    surfaceHost.style.setProperty('--agl-account-dialog-top', `${resolvedTop}px`);
  };


  #setAccountPopoverOpen = (open) => {
    if (!this.accountPopover || !this.accountPopoverTrigger || !this.accountPopoverPanel) return;
    if (open && this.#portalSession === 'unknown') return;
    this.accountPopoverPanel.hidden = !open;
    this.accountPopoverTrigger.setAttribute('aria-expanded', String(open));
    if (open) this.#positionAccountSurface(this.accountPopoverTrigger, this.accountPopover, 360);
    this.#scheduleSessionPoll();
  };

  #onAccountTriggerClick = async () => {
    if (!this.accountPopoverPanel) return;
    if (!this.accountPopoverPanel.hidden) {
      this.#setAccountPopoverOpen(false);
      return;
    }
    const epoch = this.#sessionEpoch;
    await this.#refreshPortalSession();
    if (!this.isConnected || epoch !== this.#sessionEpoch) return;
    this.#setAccountPopoverOpen(true);
  };

  #onAccountClose = () => {
    this.#setAccountPopoverOpen(false);
    this.accountPopoverTrigger?.focus();
  };

  #onDocumentClick = (event) => {
    if (!this.accountPopover || this.accountPopoverPanel?.hidden !== false) return;
    const target = /** @type {Node | null} */ (event.target);
    if (target && !this.accountPopover.contains(target)) this.#setAccountPopoverOpen(false);
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || this.accountPopoverPanel?.hidden !== false) return;
    this.#setAccountPopoverOpen(false);
    this.accountPopoverTrigger?.focus();
  };


  #onResize = () => {
    if (this.accountPopoverPanel?.hidden === false && this.accountPopover && this.accountPopoverTrigger) {
      this.#positionAccountSurface(this.accountPopoverTrigger, this.accountPopover, 360);
    }

  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
